import React, { useEffect, useState } from 'react'
import {Trash2, Download, Printer, Calendar} from 'lucide-react'
import { useSavedEstimates } from '@/hooks/useSavedEstimates'
import { useAuth } from '@/hooks/useAuth'
import jsPDF from 'jspdf'

export default function SavedEstimates() {
  const { estimates, loading, fetchEstimates, deleteEstimate } = useSavedEstimates()
  const { isAuthenticated } = useAuth()

  useEffect(() => {
    if (isAuthenticated) {
      fetchEstimates()
    }
  }, [isAuthenticated])

  const generatePDF = (estimate: any) => {
    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    
    doc.setFillColor(30, 64, 175)
    doc.rect(0, 0, pageWidth, 40, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(24)
    doc.text('Saved Roof Estimate', pageWidth / 2, 25, { align: 'center' })
    
    doc.setTextColor(0, 0, 0)
    doc.setFontSize(16)
    doc.text('Project Details', 20, 55)
    doc.setFontSize(11)
    doc.text(`Zip Code: ${estimate.zipCode}`, 20, 65)
    doc.text(`Square Footage: ${estimate.squareFootage} sq ft`, 20, 72)
    doc.text(`Material: ${estimate.material}`, 20, 79)
    doc.text(`Saved: ${new Date(estimate.createdAt).toLocaleDateString()}`, 20, 86)
    
    let yPos = 100
    doc.setFontSize(16)
    doc.text('Cost Estimate', 20, yPos)
    yPos += 15
    doc.setFillColor(249, 115, 22)
    doc.rect(15, yPos - 5, pageWidth - 30, 20, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(18)
    doc.text(`$${estimate.estimateLow.toLocaleString()} - $${estimate.estimateHigh.toLocaleString()}`, pageWidth / 2, yPos + 5, { align: 'center' })
    
    doc.setTextColor(100, 100, 100)
    doc.setFontSize(9)
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, 280)
    doc.text('www.roofingcostscalculator.com', pageWidth - 20, 280, { align: 'right' })
    
    doc.save(`Saved_Estimate_${estimate.zipCode}_${Date.now()}.pdf`)
  }

  const handlePrint = (estimate: any) => {
    const printWindow = window.open('', '_blank')
    if (!printWindow) return

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Roof Estimate - ${estimate.zipCode}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            .header { background: #1E40AF; color: white; padding: 30px; text-align: center; margin-bottom: 30px; }
            .header h1 { margin: 0; font-size: 28px; }
            .section { margin: 20px 0; }
            .section h2 { font-size: 18px; color: #1E40AF; margin-bottom: 10px; }
            .detail { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #E5E7EB; }
            .estimate-box { background: #F97316; color: white; padding: 20px; text-align: center; font-size: 24px; font-weight: bold; margin: 20px 0; }
            .footer { text-align: center; color: #6B7280; font-size: 12px; margin-top: 40px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Roof Cost Estimate</h1>
          </div>
          <div class="section">
            <h2>Project Details</h2>
            <div class="detail"><span>Zip Code:</span><span>${estimate.zipCode}</span></div>
            <div class="detail"><span>Square Footage:</span><span>${estimate.squareFootage} sq ft</span></div>
            <div class="detail"><span>Material:</span><span>${estimate.material}</span></div>
            <div class="detail"><span>Saved Date:</span><span>${new Date(estimate.createdAt).toLocaleDateString()}</span></div>
          </div>
          <div class="estimate-box">
            $${estimate.estimateLow.toLocaleString()} - $${estimate.estimateHigh.toLocaleString()}
          </div>
          <div class="footer">
            <p>Generated: ${new Date().toLocaleDateString()} | www.roofingcostscalculator.com</p>
            <p>Estimates based on industry averages. Actual costs may vary.</p>
          </div>
        </body>
      </html>
    `)
    printWindow.document.close()
    printWindow.print()
  }

  if (!isAuthenticated) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-2xl mx-auto">
        <p className="text-gray-600">Sign in to view your saved estimates</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-2xl mx-auto">
        <p className="text-gray-600">Loading your saved estimates...</p>
      </div>
    )
  }

  if (estimates.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 text-center max-w-2xl mx-auto">
        <p className="text-gray-600">No saved estimates yet. Calculate an estimate to save it!</p>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Saved Estimates</h2>
      {estimates.map((estimate) => (
        <div key={estimate._id} className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="font-bold text-xl text-gray-900 mb-2">
                {estimate.estimateName || `Estimate - ${estimate.zipCode}`}
              </h3>
              <div className="flex items-center text-sm text-gray-500">
                <Calendar size={14} className="mr-1" />
                {new Date(estimate.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-blue-800">
                ${estimate.estimateLow.toLocaleString()} - ${estimate.estimateHigh.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm text-gray-700 mb-4">
            <div><span className="font-semibold">Zip Code:</span> {estimate.zipCode}</div>
            <div><span className="font-semibold">Square Footage:</span> {estimate.squareFootage} sq ft</div>
            <div><span className="font-semibold">Material:</span> {estimate.material}</div>
            <div><span className="font-semibold">Stories:</span> {estimate.projectDetails?.numStories || 1}</div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => generatePDF(estimate)}
              className="flex-1 bg-blue-800 hover:bg-blue-900 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Download size={16} />
              Download PDF
            </button>
            <button
              onClick={() => handlePrint(estimate)}
              className="flex-1 bg-gray-700 hover:bg-gray-800 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Printer size={16} />
              Print
            </button>
            <button
              onClick={async () => {
                if (confirm('Delete this estimate?')) {
                  await deleteEstimate(estimate._id)
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}
